import React from "react";
import SignaturePad from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useActiveJob } from "@/contexts/ActiveJobContext";
import { replaceContractPlaceholders } from "@/utils/pdfUtils";
import { useApplication } from "@/contexts/ApplicationContext";
import { format } from "date-fns";

interface ContractReviewFormProps {
  onSign: (signature: string | null) => void;
  signature: string | null;
}

const ContractReviewForm: React.FC<ContractReviewFormProps> = ({
  onSign,
  signature,
}) => {
  const { job } = useActiveJob();
  const { application } = useApplication();
  const signaturePad = React.useRef(null);

  const clear = () => {
    if (signaturePad.current) {
      signaturePad.current.clear();
      onSign(null);
    }
  };

  const save = () => {
    if (signaturePad.current && !signaturePad.current.isEmpty()) {
      const signatureData = signaturePad.current.toDataURL();
      onSign(signatureData);
    }
  };

  const address = application?.personalDetails?.address?.line1
    ? application?.personalDetails?.address?.line1 +
      ", " +
      application?.personalDetails?.address?.city +
      ", " +
      application?.personalDetails?.address?.postcode
    : "";
  const fullName =
    application?.basicDetails?.firstName +
    " " +
    application?.basicDetails?.lastName;
  const driverNumber = application?.airtablePersonId;
  const companyName = application?.personalDetails?.company?.number || fullName;
  const vatNumber = application?.personalDetails?.company?.vatNumber;
  const currentStage = job.stages.find((stage) => stage.id === "contract");
  const contractText = currentStage?.meta?.contract as string;
  const defaultSalary = currentStage?.meta?.defaultSalary as string;
  const contract = replaceContractPlaceholders(contractText || "", {
    "full-name": fullName,
    "driver-number": driverNumber,
    "company-name-or-full-name": companyName,
    address: address,
    "vat-number": vatNumber,
    salary: application?.contractSalary || defaultSalary,
    date: format(new Date(), "MMMM d, yyyy"),
  });

  return (
    <div className="space-y-6">
      <div className="border rounded-lg bg-white shadow-sm">
        <div className="p-6 border-b bg-gray-50">
          <h3 className="text-xl font-semibold text-jungle">
            Delivery Driver Contract
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Please read the contract carefully before signing
          </p>
        </div>

        <div className="h-96 overflow-y-auto p-6 space-y-4 text-base leading-relaxed">
          {contract ? (
            <div
              className="whitespace-pre-wrap contract-content prose max-w-full w-full"
              dangerouslySetInnerHTML={{ __html: contract }}
            />
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">Contract not available</p>
            </div>
          )}
        </div>
        <div className="p-4 border-t bg-gray-50">
          <p className="text-gray-700 font-medium">
            By signing below, the Driver acknowledges that they have read,
            understood, and agree to all terms and conditions of this Agreement.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <Label className="text-lg font-medium">Your Signature</Label>
        {signature ? (
          <div className="space-y-3">
            <div className="border bg-white p-4 rounded-md shadow-sm">
              <img
                src={signature}
                alt="Signature"
                className="max-h-40 w-full object-contain"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSign(null)}
              className="w-full md:w-auto"
            >
              Clear Signature
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="border bg-white rounded-md shadow-sm touch-none">
              <SignaturePad
                ref={signaturePad}
                canvasProps={{
                  className: "w-full h-40 touch-none",
                }}
              />
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clear}
                className="w-full md:w-auto"
              >
                Clear
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={save}
                className="w-full md:w-auto bg-honeycomb hover:bg-amber-500"
              >
                Sign Contract
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractReviewForm;
